/**
 * One column of the curriculum browser.
 *
 * A Finder-style column list rather than a tree: the curriculum is six levels
 * deep, and an indented tree at that depth is unreadable by the third one — the
 * indentation alone eats half the width. Columns keep every level at full width
 * and make the path you are on visible as a row of selections, which is exactly
 * what an admin is trying to hold in their head.
 */
import type { ReactNode } from 'react';
import type { BinColor } from '@cwy/config/contracts';
import { binRamp } from '@/lib/bins';

export interface FinderItem {
  id: string;
  label: string;
  /** Secondary text on the right — a count, or what a selection covers. */
  meta?: string;
  /** Present on selectable columns; absent turns the checkbox off entirely. */
  checked?: boolean;
  /** Some but not all descendants selected. */
  partial?: boolean;
  /** No children to drill into, so no chevron. */
  leaf?: boolean;
}

export interface FinderColumnProps {
  title: string;
  items: FinderItem[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  onToggle?: (id: string) => void;
  /** Rendered under the list — the "add" control on an authoring surface. */
  footer?: ReactNode;
  /**
   * Rename this row. Shown on the selected row only: one affordance per row
   * would put a control against every chapter in a fourteen-row column, and the
   * product is deliberately icon-light.
   */
  onRename?: (id: string) => void;
  /** Remove this row. Selected row only, for the same reason as rename. */
  onRemove?: (id: string) => void;
  emptyLabel?: string;
  /** Platform authoring uses separate coloured blocks; school selection keeps its compact columns. */
  tone?: BinColor;
  step?: string;
  className?: string;
}

export function FinderColumn({
  title,
  items,
  selectedId,
  onSelect,
  onToggle,
  footer,
  onRename,
  onRemove,
  emptyLabel = 'Nothing here yet',
  tone,
  step,
  className = '',
}: FinderColumnProps) {
  const ramp = binRamp(tone ?? 'BLUE');
  return (
    <section
      className={`flex flex-1 flex-col ${tone ? 'min-w-0 overflow-hidden rounded-card border border-line bg-card' : 'min-w-[200px] border-r border-line last:border-r-0'} ${className}`}
      aria-label={title}
    >
      <div
        className={`flex items-center gap-2 border-b border-line ${tone ? 'px-3 py-3' : 'px-3 py-2'}`}
        style={tone ? { background: ramp.bg, color: ramp.text } : undefined}
      >
        {step && (
          <span
            className="flex size-6 shrink-0 items-center justify-center rounded-lg border border-current text-fs-10 font-bold"
            aria-hidden="true"
          >
            {step}
          </span>
        )}
        <h3
          className={
            tone
              ? 'text-xs font-bold'
              : 'text-fs-11 font-bold uppercase tracking-wide text-ink-soft'
          }
        >
          {title}
        </h3>
        {tone && (
          <span className="ml-auto rounded-full bg-card px-2 py-0.5 text-fs-10 font-semibold text-ink-soft">
            {items.length}
          </span>
        )}
      </div>

      <div className={`min-h-0 flex-1 overflow-y-auto ${tone ? 'space-y-1 p-2' : ''}`}>
        {items.length === 0 && <p className="px-3 py-3 text-xs text-ink-soft">{emptyLabel}</p>}

        {items.map((item) => {
          const active = item.id === selectedId;
          return (
            <div
              key={item.id}
              className={`flex flex-wrap items-center gap-2 transition-colors duration-150 ${tone ? 'rounded-lg border px-2.5 py-2.5' : 'px-3 py-1.5'}`}
              style={{
                background: active ? ramp.bg : 'transparent',
                color: active ? ramp.text : 'var(--text-body)',
                borderColor: active ? ramp.border : 'transparent',
              }}
            >
              {onToggle && item.checked !== undefined && (
                <input
                  type="checkbox"
                  className="size-3.5 shrink-0 accent-forest"
                  checked={item.checked}
                  // A partially-selected parent shows the browser's own
                  // indeterminate mark, which is set through the DOM node — the
                  // `indeterminate` property has no HTML attribute.
                  ref={(node) => {
                    if (node) node.indeterminate = Boolean(item.partial) && !item.checked;
                  }}
                  onChange={() => onToggle(item.id)}
                  aria-label={`Offer ${item.label}`}
                />
              )}

              <button
                type="button"
                className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left"
                onClick={() => onSelect?.(item.id)}
                disabled={!onSelect}
                aria-current={active ? 'true' : undefined}
                title={item.label}
              >
                <span className={`${tone ? 'break-words' : 'truncate'} text-fs-13 font-semibold`}>
                  {item.label}
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {item.meta && (
                    <span className="text-fs-11 font-medium text-ink-soft">{item.meta}</span>
                  )}
                  {!item.leaf && onSelect && (
                    <span className="text-fs-11 text-ink-soft" aria-hidden>
                      ›
                    </span>
                  )}
                </span>
              </button>

              {active && (onRename || onRemove) && (
                <div className={tone ? 'flex w-full items-center justify-end gap-3' : 'contents'}>
                  {onRename && active && (
                    <button
                      type="button"
                      className="shrink-0 text-fs-11 font-semibold underline"
                      onClick={() => onRename(item.id)}
                      aria-label={`Rename ${item.label}`}
                    >
                      Rename
                    </button>
                  )}
                  {onRemove && active && (
                    <button
                      type="button"
                      className="shrink-0 text-fs-11 font-semibold text-danger underline"
                      onClick={() => onRemove(item.id)}
                      aria-label={`Remove ${item.label}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {footer && <div className="border-t border-line bg-paper p-2">{footer}</div>}
    </section>
  );
}
